import { CommonModule } from '@angular/common';
import { Component, computed, effect, HostListener, inject, OnInit, Renderer2, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { CwIcon, CwNavigationRail, CwNavigationRailItem } from '@bbraun/cortex-angular';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, switchMap, take } from 'rxjs';
import { HeaderComponent } from './components/header/header.component';
import { MobileDrawerComponent } from './components/mobile-drawer/mobile-drawer.component';
import { ModalContainerComponent } from './components/modals/modal-container/modal-container.component';
import { OverlayComponent } from './components/overlay/overlay.component';
import { SidebarContainerComponent } from './components/sidebar-container/sidebar-container.component';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { Status } from './constants/AppStatuses';
import { ConnectionType } from './enums/ConnectionType';
import { AuthService } from './services/auth.service';
import { ConfigService } from './services/config.service';
import { DialogService } from './services/dialog.service';
import { HeaderService } from './services/header.service';
import { LocaleService } from './services/locale.service';
import { NavigationService } from './services/navigation.service';
import { OverlayService } from './services/overlay.service';
import { SidebarService } from './services/sidebar.service';
import { SplashAnimationService } from './services/splash-animation.service';
import { StatusService } from './services/status.service';
import { ToastService } from './services/toast.service';
import { UserService } from './services/user.service';
import { WsEventsService } from './services/websocket.service';
import { TherapyJobStore } from './store/job.store';
import { UiStore } from './store/ui.store';
import { EventManager } from './utils/EventManager';

@Component({
  selector: 'gplus-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    RouterModule,
    TranslateModule,
    CwNavigationRail,
    CwNavigationRailItem,
    ModalContainerComponent,
    HeaderComponent,
    OverlayComponent,
    MobileDrawerComponent,
    SidebarContainerComponent,
    ToastContainerComponent,
    CommonModule,
    CwIcon
  ]
})
export class AppComponent implements OnInit {
  // Public properties for template
  public readonly dialogService = inject(DialogService);
  public readonly overlayService = inject(OverlayService);
  public readonly sidebarService = inject(SidebarService);
  public readonly toastService = inject(ToastService);
  public readonly headerService = inject(HeaderService);
  public readonly statusService = inject(StatusService);
  public readonly splashAnimationService = inject(SplashAnimationService);
  public readonly userService = inject(UserService);

  public readonly status = Status;

  // Private services - Core
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly renderer = inject(Renderer2);
  private readonly localeService = inject(LocaleService);

  // Private services - Business logic
  private readonly configService = inject(ConfigService);
  private readonly navigationService = inject(NavigationService);
  private readonly wsEventsService = inject(WsEventsService);
  private readonly eventManager = inject(EventManager);

  // Private stores
  private readonly jobsStore = inject(TherapyJobStore);
  private readonly uiStore = inject(UiStore);

  public readonly navigationItems = toSignal(this.navigationService.getRoutes(), { initialValue: [] });
  private readonly pollingSignal: Signal<boolean> = computed(
    () => this.configService.configuration()?.content?.general?.enablePollingMode
  );

  @HostListener('window:resize')
  onResize() {
    this.headerService.setMobileMenuBtn(false);
    this.uiStore.setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    this.wsEventsService.close();
  }

  constructor() {
    this.uiStore.setWindowSize({ width: window.innerWidth, height: window.innerHeight });

    effect(() => this.handleSplashScreen());
    effect(() => this.syncConnectionWithConfigAndStatus());
    effect(() => this.pollData());

    this.setupNavigationListener();
  }

  private handleSplashScreen(): void {
    if (!this.splashAnimationService.show()) {
      this.hideSplashScreen();
    }
  }

  private syncConnectionWithConfigAndStatus(): void {
    const isWSAlive = this.statusService.isWSAliveSignal();
    const enablePollingMode = this.pollingSignal();

    if (
      enablePollingMode === undefined ||
      enablePollingMode === null ||
      isWSAlive === undefined ||
      isWSAlive === null
    ) {
      // configuration or status not ready yet, cannot determine connection type
      return;
    }

    const enableWSConnection = !enablePollingMode && isWSAlive;

    this.toggleConnection(enableWSConnection);
  }

  private pollData(): void {
    const isTicking = this.statusService.ticker();
    const isPolling = this.jobsStore.connectionType() === ConnectionType.POLLING;

    if (isTicking && isPolling) {
      this.jobsStore.load(false);
    }
  }

  ngOnInit(): void {
    this.authService.configureAuth();
    this.initializeApplication();
  }

  private toggleConnection(enableWSConnection: boolean): void {
    if (!enableWSConnection) {
      console.warn('Switching to polling mode');
      this.jobsStore.setConnectionType(ConnectionType.POLLING);

      this.wsEventsService.close();
      this.eventManager.destroySubscription();
    } else {
      this.jobsStore.setConnectionType(ConnectionType.WS);

      this.wsEventsService.connect();
      this.eventManager.initSubscription();
    }
  }

  private setupNavigationListener(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.headerService.setMobileMenuBtn(false));
  }

  private initializeApplication(): void {
    this.authService.isLogged
      .pipe(
        take(1),
        switchMap(() => this.userService.getMe()),
        switchMap((me) => this.localeService.applyLanguage().pipe(map(() => me)))
      )
      .subscribe((me) => {
        if (me) {
          this.splashAnimationService.hideSplash();
          this.startBackgroundServices();
        }
      });
  }

  private hideSplashScreen(): void {
    const splashElement = document.getElementById('splash-animation');
    if (splashElement) {
      this.renderer.addClass(splashElement, 'fadeout');
    }
  }

  private startBackgroundServices(): void {
    this.configService.getConfig().pipe(take(1)).subscribe();
  }

  public linkClicked(): void {
    this.headerService.setMobileMenuBtn(false);
  }
}
